"use client";

import { useSyncExternalStore } from "react";

const STORAGE_PREFIX = "workspace-welcome";
const LEGACY_DEMO_PREFIX = "openwork-demo-state";

type WorkspaceWelcomeState = {
  introCompleted: boolean;
};

const EMPTY_STATE: WorkspaceWelcomeState = { introCompleted: false };

let storageKey = "";
let cachedRaw: string | null = null;
let cachedState: WorkspaceWelcomeState = EMPTY_STATE;
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((listener) => listener());
}

function readStorageKey(userKey: string): string {
  return `${STORAGE_PREFIX}:${userKey}`;
}

function readLegacyIntroCompleted(userKey: string): boolean | null {
  if (typeof window === "undefined" || !userKey) return null;
  const legacyRaw = window.localStorage.getItem(`${LEGACY_DEMO_PREFIX}:${userKey}`);
  if (!legacyRaw) return null;
  try {
    const parsed = JSON.parse(legacyRaw) as { introCompleted?: boolean; unlockedScenarioRefs?: string[] };
    if (typeof parsed.introCompleted === "boolean") return parsed.introCompleted;
    if (Array.isArray(parsed.unlockedScenarioRefs) && parsed.unlockedScenarioRefs.length > 0) {
      return true;
    }
  } catch {
    return null;
  }
  return null;
}

function parseState(raw: string | null, userKey: string): WorkspaceWelcomeState {
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as Partial<WorkspaceWelcomeState>;
      if (typeof parsed.introCompleted === "boolean") {
        return { introCompleted: parsed.introCompleted };
      }
    } catch {
      // fall through to legacy migration
    }
  }

  const legacyIntroCompleted = readLegacyIntroCompleted(userKey);
  if (legacyIntroCompleted !== null) {
    return { introCompleted: legacyIntroCompleted };
  }

  return EMPTY_STATE;
}

function loadState(): WorkspaceWelcomeState {
  if (!storageKey || typeof window === "undefined") return cachedState;
  const raw = window.localStorage.getItem(storageKey);
  if (raw === cachedRaw) return cachedState;
  cachedRaw = raw;
  const userKey = storageKey.slice(STORAGE_PREFIX.length + 1);
  cachedState = parseState(raw, userKey);
  return cachedState;
}

function writeState(next: WorkspaceWelcomeState) {
  if (!storageKey || typeof window === "undefined") {
    cachedState = next;
    cachedRaw = JSON.stringify(next);
    notify();
    return;
  }
  cachedState = next;
  cachedRaw = JSON.stringify(next);
  window.localStorage.setItem(storageKey, cachedRaw);
  notify();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function configureWorkspaceWelcomeStorage(userKey: string | null | undefined) {
  const nextKey = userKey ? readStorageKey(userKey) : "";
  if (nextKey === storageKey) return;
  storageKey = nextKey;
  cachedRaw = null;
  cachedState = EMPTY_STATE;
  if (storageKey) loadState();
  notify();
}

export function readWorkspaceWelcomeState(): WorkspaceWelcomeState {
  return loadState();
}

export function completeWorkspaceWelcome() {
  writeState({ introCompleted: true });
}

export function resetWorkspaceWelcome() {
  writeState({ introCompleted: false });
}

export function useWorkspaceWelcomeState() {
  return useSyncExternalStore(subscribe, readWorkspaceWelcomeState, () => EMPTY_STATE);
}
